import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDisclosure } from '@mantine/hooks';
import { Anchor, Group, Popover, ScrollArea, Text, Title } from '@mantine/core';
import addAlert from 'ps-components/AlertToast/addAlert';
import { IAppState } from '../../../../lib/types/AppState/index';
import { INode } from '../Types/BulkPricingTypes';
import { getFieldData, formatDataCuts } from '../utils';
import { formatDate } from '../../../../ps-components/AdvancedTable/GenericGrid/GenericGridCellRenderers';
import { showSlideout } from '../../../../ps-components/Slideout/SlideoutReducer';
import { getDataCuts } from '../BulkPricingAPIs';
import { SLIDEOUTS as MP_SLIDEOUTS } from '../../../Slideout/Slideouts';

export interface ISurveyDescriptorProps {
  isSurveyPanel: boolean;
  onDataCutListChange?: (dataCuts: INode[]) => void;
  surveyJob: INode;
  setCheckedDataCuts?: (dataCuts: INode[] | ((pending: INode[]) => INode[])) => void;
};

export default function SurveyDescriptor({
  isSurveyPanel,
  onDataCutListChange,
  surveyJob,
  setCheckedDataCuts
}: ISurveyDescriptorProps) {
  const dispatch = useDispatch();
  const [opened, { close, open }] = useDisclosure(false);
  const [popoverHovered, setPopoverHovered] = useState(false);
  const [titleHovered, setTitleHovered] = useState(false);

  const benchmarkYear = useSelector((state: IAppState) => state.session.benchmarkYear);
  const displayIn = useSelector((state: IAppState) => state.session.monetary_display);
  const masterAccountID = useSelector((state: IAppState) => state.session.organization.masterAccountId);
  const monetaryRounding = useSelector((state: IAppState) => state.session.user.preferences.monetaryRounding);

  const dataCuts = surveyJob.children || [];
  const selectedDataCutIds = dataCuts.map((dataCut: INode) => dataCut.id as number);
  const terms = surveyJob.terms;
  const firstDataCut = dataCuts[0]?.data;
  const effectiveDate = getFieldData('effective_date_default', firstDataCut, terms) || getFieldData('pub_survey_effective_date_default', firstDataCut, terms);
  const jobCode = getFieldData('job_code_default', firstDataCut, terms);
  const jobDescription = getFieldData('job_description_default', firstDataCut, terms);
  const jobTitle = getFieldData('job_title_default', firstDataCut, terms);
  const surveyCode = getFieldData('survey_code_default', firstDataCut, terms);
  const surveyCurrency = getFieldData('currency_default', firstDataCut, terms);
  const surveyName = getFieldData('survey_short_name_default', firstDataCut, terms) || getFieldData('pub_survey_short_name_default', firstDataCut, terms);

  useEffect(() => {
    if (opened) {
      const timer = setTimeout(() => {
        if (!popoverHovered && !titleHovered) close();
      }, 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [opened, popoverHovered, titleHovered]);

  function handleDataCutSlideoutChange(dataCutIds: number[]) {
    if (!dataCutIds.length) return;

    const newCuts = dataCutIds.map((id: number) => ({
      cutId: id,
      pubSurveyCode: getFieldData('survey_code_default', firstDataCut, terms),
      surveyJobCode: getFieldData('job_code_default', firstDataCut, terms)
    }));

    getDataCuts({ masterAccountID, benchmarkYear, currency: '%', displayIn, newCuts }).then((resp) => {
      const formattedDataCuts = formatDataCuts(resp?.data?.data, terms);
      onDataCutListChange?.(formattedDataCuts);
    });
  }

  function showSurveyDataCutsSlideout() {
    dispatch(showSlideout(MP_SLIDEOUTS.SURVEY_DATA_CUTS, {
      benchmarkYear,
      displayIn,
      monetaryRounding,
      onSave: (addedCutIds: number[], removedCutIds: number[]) => {
        const updatedDataCutIds = selectedDataCutIds
          .concat(addedCutIds)
          .filter((id: number) => !removedCutIds.includes(id));

        if (!updatedDataCutIds.length) return addAlert('Changes not saved. At least one data cut needs to be selected', 'warning', 4000);
        handleDataCutSlideoutChange(updatedDataCutIds);
        if (setCheckedDataCuts) setCheckedDataCuts(pending => pending.filter(cut => !removedCutIds.includes(cut.id as number)));
      },
      selectedSurveyIds: selectedDataCutIds,
      selectionChangeType: "data cut",
      shouldNotPersist: true,
      survey: {
        currency: surveyCurrency,
        job_code_default: jobCode,
        job_title_default: jobTitle,
        survey_code_default: surveyCode,
        survey_long_name_default: surveyName,
        survey_short_name_default: surveyName,
        job_description_default: jobDescription
      }
    }));
  }

  return (
    <>
      <Group className='survey-job'>
        <Popover opened={opened} position="bottom" shadow="xl" withArrow>
          <Popover.Target>
            <Title
              c='skyblue'
              className='survey-job-title'
              lineClamp={1}
              mb='5px'
              onMouseEnter={() => {
                setTitleHovered(true);
                open();
              }}
              onMouseLeave={() => {
                setTitleHovered(false);
              }}
              order={6}
            >
              {jobTitle}
            </Title>
          </Popover.Target>
          <Popover.Dropdown
            maw={700}
            onMouseEnter={() => setPopoverHovered(true)}
            onMouseLeave={() => {
              setPopoverHovered(false);
              close();
            }}
          >
            <Text fw={700} size="sm">{jobTitle}</Text>
            <ScrollArea h={jobDescription?.length > 700 ? 300 : ''}>
              <Text mr='20px' size="sm">{jobDescription}</Text>
            </ScrollArea>
          </Popover.Dropdown>
        </Popover>
        <Text c='dark' miw={100} size='sm' truncate='end'>{jobCode}</Text>
      </Group>
      <Group align='center' ml='lg'>
        <Text c='dimmed' size='sm'>{`${surveyCode}, ${surveyName}, eff ${formatDate(effectiveDate)}`}</Text>
        {isSurveyPanel && <Anchor c="skyblue" onClick={() => showSurveyDataCutsSlideout()} size='sm' ta='right'>Add data cut</Anchor>}
      </Group>
    </>
  );
}
